import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { LaunchpadUser, LaunchpadUserDto } from '../models/launchpad-user';
import { map, Observable } from 'rxjs';
import { Queries } from '../queries';
import { UserInput } from '../models/user-input';

@Injectable({
  providedIn: 'root'
})
export class UsersAdminService {

  constructor(private apollo: Apollo) { }

  getUsersForOrganisation(
    organisations: string[] | null,
    canImpersonate: boolean = false,
    searchTerm: string = '',
    onlyActive: boolean,
    onlyImpersonationRequested: boolean
  ): Observable<LaunchpadUser[]> {
    return this.apollo.watchQuery<{organisationUsers: LaunchpadUserDto[]}>({
      query: Queries.GET_USERS,
      fetchPolicy: 'no-cache',
      variables: {
        organisations: organisations,
        searchTerm: searchTerm,
        canImpersonate: canImpersonate,
        activeStatusQuery: onlyActive
          ? { isActive: { eq: true } }
          : { },
        impersonationQuery: canImpersonate && onlyImpersonationRequested
          ? { impersonationExpiry: { gt: new Date() } }
          : { }
      }
    }).valueChanges.pipe(map(result => {
      let users = result.data.organisationUsers.map(user => LaunchpadUser.fromLaunchpadUserDto(user));

      return users ?? [];
    }))
  }

  getUser(userId: string): Observable<LaunchpadUser> {
    return this.apollo.watchQuery<{user: LaunchpadUserDto}>({
      query: Queries.GET_USER,
      fetchPolicy: 'no-cache',
      variables: {
        userId: userId
      }
    }).valueChanges.pipe(map(result => LaunchpadUser.fromLaunchpadUserDto(result.data.user)))
  }

  createUser(user: Partial<UserInput>): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{createUser: LaunchpadUser}>({
      mutation: Queries.CREATE_USER,
      variables: {
        user: user
      }
    }).pipe(map(result => result.data?.createUser))
  }

  updateUser(user: Partial<UserInput>): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{updateUser: LaunchpadUser}>({
      mutation: Queries.UPDATE_USER,
      variables: {
        user: user
      }
    }).pipe(map(result => result.data?.updateUser))
  }
  
  activateUser(userEmail: string): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{activateUser: LaunchpadUser}>({
      mutation: Queries.ACTIVATE_USER,
      variables: {
        userEmail: userEmail
      }
    }).pipe(map(result => result.data?.activateUser))
  }

  deactivateUser(userEmail: string): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{deactivateUser: LaunchpadUser}>({
      mutation: Queries.DEACTIVATE_USER,
      variables: {
        userEmail: userEmail
      }
    }).pipe(map(result => result.data?.deactivateUser))
  }

  resendUserInvite(userEmail: string): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{inviteUser: LaunchpadUser}>({
      mutation: Queries.RESEND_USER_INVITE,
      variables: {
        userEmail: userEmail
      }
    }).pipe(map(result => result.data?.inviteUser))
  }
}
